// medplum-reports.js — ONLINE-PRIMARY, same recipe as the other 4 screens.
// Reports.jsx is CLINICAL-only; reads as the ONE logged-in session.
//
// PAGINATION (2026-07-07): the reports drawer is SERVER-SIDE PAGED, mirroring
// the Patients board (medplum-patients.js). The old query pulled up to 500
// DocumentReferences in one payload (_include Encounter+Patient, _count=500) —
// on a busy clinic that is a large first paint that grows unbounded. We now
// fetch ONE page of DocumentReferences at a time with an accurate total.
//
// WHY PAGE FILES, NOT PATIENTS (the strategy, with docs evidence):
//   The board GROUPS by patient, so a naive file-page would split one patient's
//   files across a page edge. Two ways to keep groups whole:
//     (a) page DISTINCT patients-with-files via a reverse-chained search
//         (Patient?_has:DocumentReference:patient:…). REJECTED: the _has
//         existence sub-query is evaluated against the project, NOT the caller's
//         RLS scope — measured `Patient?_has:DocumentReference:patient:status=current
//         &_total=accurate` returned 450 on a stack where the doctor can read
//         docs for only 12 patients, and its page-1 (name-sorted) patients had
//         ZERO doctor-readable docs. So the pager's "of N" would lie and most
//         pages would render empty. (Medplum reverse-chain / _has is documented
//         in medplum-link/…/docs/search/chained-search.md.)
//     (b) page DocumentReferences sorted by `patient`, group per page. CHOSEN.
//         `patient`/`subject` are REFERENCE search params (per the resource
//         definition medplum-link/…/resourceDefinitions/documentreference.json)
//         and `_sort` accepts any search parameter (…/docs/search/basic-search.mdx
//         §"Sorting the Results"). Sorting by `patient` makes every patient's
//         files CONTIGUOUS in the result stream — verified live: on the 162-doc
//         seed, `_sort=patient,-date&_count=25` returned 3 patient runs === 3
//         distinct patients (no interleaving), newest-first within each patient.
//         `_total=accurate` gives the pager its honest, RLS-scoped "of N".
//   The one seam of (b): a patient whose files straddle a page edge. We surface
//   it honestly — the shaper marks a boundary group `continues:true` and the
//   screen shows a "more files on the next page" note rather than pretending the
//   group is complete. One round-trip per page; no over-count, no empty pages.
//
// TYPE TABS (All/PDFs/Images/Audio) filter SERVER-SIDE via the `contenttype`
// TOKEN param (comma-OR over the known MIME set — token OR is documented in
// basic-search.mdx). SEARCH by patient name / MRN is server-side too, via the
// `patient` reference chained on name/identifier. KPI tiles come from cheap
// `_summary=count&_total=accurate` probes per tab (no full load), same idiom as
// medplum-settings.js.
//
// The server's per-row `canRead` chart-read-gate re-check (C5) stays dropped —
// same reasoning as patients-view.js: RLS already scoped this DocumentReference
// search to what the doctor may read.
import { getMedplum, deriveCurrentUser } from './client-port.js';
import { groupReportsBundle, shapeReportRow, latestConsultOf } from '@healther/clinical-core/shape/reports-view.js';

// One page = this many FILES (DocumentReferences). Group sizes vary (a patient
// may have 1–30 files), so a file-page yields a variable number of patient
// cards — a comfortable ~2–5 groups per page for typical clinics.
export const REPORTS_PAGE_SIZE = 30;

// Known clinical MIME types per tab. `contenttype` is an exact-match token
// param (no prefix match), so we OR the concrete types the app actually writes
// (paper-rx PDFs, patient JPEG/PNG uploads, consult WebM/MP3 audio) rather than
// rely on a `image/*` glob FHIR tokens don't support.
const MIME_BY_TAB = {
  pdf:   ['application/pdf'],
  image: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/tiff'],
  audio: ['audio/webm', 'audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/ogg', 'audio/x-m4a'],
};

// Escapes a term for a FHIR _filter string literal (double-quote delimited).
const filterLiteral = (s) => String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

// A single cheap count-only probe (Medplum returns just Bundle.total).
async function countDocs(medplum, extraParam) {
  const p = new URLSearchParams();
  p.set('_summary', 'count');
  p.set('_total', 'accurate');
  if (extraParam) for (const [k, v] of extraParam) p.append(k, v);
  const b = await medplum.search('DocumentReference', p.toString());
  return typeof b.total === 'number' ? b.total : 0;
}

/**
 * Loads ONE page of the clinic-wide reports drawer as the logged-in user.
 *
 * @param {object} opts
 * @param {number} opts.page      1-based page number (default 1).
 * @param {number} opts.pageSize  files per page (default REPORTS_PAGE_SIZE).
 * @param {string} opts.filter    'all' | 'pdf' | 'image' | 'audio' (type tab).
 * @param {string} opts.search    free-text patient name / MRN (server-side).
 *
 * Returns the DTO the screen consumes, now paging-aware:
 *   { source, reports: { groups, rows, counts, page, pageSize, total, paged },
 *     currentUser, hospital }
 * `total` is the FILE count for the current tab/search (the pager's "of N").
 * `groups` are the patient cards for THIS page only, contiguous and ordered;
 * a group split across the page edge carries `continuesNext`/`continuesPrev`.
 *
 * Bridge SWR calls this with no args → page 1, all types, no search (only page
 * 1 rides the cache). Page>1 / tab / search are driven by the screen calling
 * this directly.
 */
export async function loadReportsFromMedplum(opts = {}) {
  const { medplum, hospital } = await getMedplum();
  const page = Math.max(1, Number(opts.page) || 1);
  const pageSize = Number(opts.pageSize) || REPORTS_PAGE_SIZE;
  const filter = opts.filter || 'all';
  const search = String(opts.search || '').trim();

  // Server-side type-tab filter: comma-OR the known MIME types for the tab.
  const contentTypeParam = MIME_BY_TAB[filter] ? MIME_BY_TAB[filter].join(',') : null;
  // Server-side patient search: chain the DocumentReference `patient` reference
  // onto the Patient's name / identifier so a name or MRN both match.
  const searchFilterParam = search
    ? `patient.name co "${filterLiteral(search)}" or patient.identifier co "${filterLiteral(search)}"`
    : null;

  // ── The one paged query ───────────────────────────────────────────────────
  // _sort=patient,-date keeps each patient's files contiguous (groups never
  // interleave) and newest-first within a patient; _total=accurate feeds the
  // pager; Encounter+Patient fan in via _include for the row/group shape.
  // The trailing `_id` is a UNIQUE tiebreaker: without it, files sharing the
  // same patient+date (two uploads in the same second — routine in a clinic)
  // make _offset paging non-deterministic, and the same row can appear on two
  // pages while another is never shown (caught live by opd-smoke2/02, W7).
  const params = new URLSearchParams();
  params.set('_count', String(pageSize));
  params.set('_offset', String((page - 1) * pageSize));
  params.set('_total', 'accurate');
  params.set('_sort', 'patient,-date,_id');
  params.append('_include', 'DocumentReference:encounter');
  params.append('_include', 'DocumentReference:patient');
  if (contentTypeParam) params.set('contenttype', contentTypeParam);
  if (searchFilterParam) params.set('_filter', searchFilterParam);

  const bundle = await medplum.search('DocumentReference', params.toString());
  const total = typeof bundle.total === 'number' ? bundle.total : null;
  // groupReportsBundle preserves DocumentReference order from the bundle (which
  // is `_sort=patient,-date`), so the docs stay patient-contiguous below.
  const { encounters, patients, docs } = groupReportsBundle(bundle);

  // Shape rows in server order (already patient-contiguous, newest-first).
  const rows = docs.map((d) => shapeReportRow(d, encounters, patients));

  // Group this page's rows by patient, PRESERVING the contiguous order so the
  // cards render top-to-bottom as the server returned them.
  const groups = [];
  const byKey = new Map();
  for (const r of rows) {
    const key = r.mrn || r.patientName;
    let g = byKey.get(key);
    if (!g) {
      g = { key, patientName: r.patientName, mrn: r.mrn, initials: r.patientInitials, age: r.age, sex: r.sex, rows: [] };
      byKey.set(key, g);
      groups.push(g);
    }
    g.rows.push(r);
  }
  // Per-group "latest openable consult" for the group-header Open-consult
  // affordance — computed from rows already loaded (no extra query). Rows are
  // patient-contiguous + newest-first, so the first row with an encounterId is
  // the most recent encounter-linked consult; patients with none get null and
  // the header omits the link.
  for (const g of groups) g.latestConsult = latestConsultOf(g.rows);

  // Boundary seam (strategy (b)): if this is not the last page, the LAST group
  // may have more files on the next page (they straddle the _offset edge). Mark
  // it so the screen can show an honest "more files on the next page" note
  // instead of implying the group is complete. Symmetrically, page>1's FIRST
  // group is a continuation of the previous page's last group.
  const lastPage = total != null ? page * pageSize >= total : docs.length < pageSize;
  if (groups.length && !lastPage) groups[groups.length - 1].continuesNext = true;
  if (groups.length && page > 1) groups[0].continuesPrev = true;

  // ── KPI tiles: cheap count-only probes (no full load) ─────────────────────
  // total files, plus per-type counts for the same search context. A distinct
  // "patients with files" count is NOT cheaply knowable without scanning the
  // whole set (the same trade-off Patients.jsx makes for with/without-encounter),
  // so we report `patients: null` and the screen simply omits that KPI tile
  // rather than show a page-scoped number that reads as clinic-wide.
  const baseCount = [];
  if (searchFilterParam) baseCount.push(['_filter', searchFilterParam]);
  const [totalAll, pdf, image, audio] = await Promise.all([
    countDocs(medplum, baseCount),
    countDocs(medplum, [...baseCount, ['contenttype', MIME_BY_TAB.pdf.join(',')]]),
    countDocs(medplum, [...baseCount, ['contenttype', MIME_BY_TAB.image.join(',')]]),
    countDocs(medplum, [...baseCount, ['contenttype', MIME_BY_TAB.audio.join(',')]]),
  ]);
  const counts = { total: totalAll, pdf, image, audio, patients: null };

  // No generatedAt: the payload must be deterministic for a given data state —
  // the SWR layer diffs cached vs fresh by JSON equality, and a per-call
  // timestamp would make every revalidate look like a change.
  return {
    source: 'medplum-client-shaping',
    reports: {
      groups, rows, counts,
      page, pageSize,
      total: total != null ? total : rows.length,
      paged: true,
      filter, search,
    },
    currentUser: await deriveCurrentUser(medplum), hospital,
  };
}
